import { all, call, fork, put, select, takeLeading } from "typed-redux-saga";
import { Action, profileSettingsActions } from "./profile-settings-store";
import { errorActions as errorActions } from "src/components/error-viewer/error-store";
import { ContactPersonPictureService, Shared_PictureResolutions } from "src/backend/openapi/v1";
import { overlayedFetch } from "@itk/mui-components";
import { baseUrl } from "src/utils";
import { getManualFetchHeaders } from "src/utils/request-headers-utils";
import { applicationActions } from "src/components/application-store";
import { RootState } from "src/store";
import { fileToBase64 } from "src/utils/file-utils";
import { deleteCache, getCache, saveCache } from "src/utils/cache-utils";
import { CACHE_KEYS } from "src/utils/constants";

export function* getContactPersonPicture(action: ReturnType<typeof profileSettingsActions.getContactPersonPictureRequest>) {
  try {
    let picture = "";
    const cacheKey = CACHE_KEYS.CONTACT_PERSON_PICTURE(action.payload.contactPersonId, action.payload.resolution);

    const cachedResponse = yield* call(() => getCache(cacheKey));

    if (cachedResponse) {
      picture = yield* call(() => cachedResponse.json() as unknown as string);
    } else {
      picture = yield* call(ContactPersonPictureService.getApiV1ContactPersonPictures, action.payload.resolution, action.payload.contactPersonId);
      yield* call(() => saveCache(cacheKey, picture));
    }

    action.payload.callBack(true, picture);
  } catch (error) {
    yield put(errorActions.addError(error));
    action.payload.callBack(false);
  }
}

export function* watchgetContactPersonPicture() {
  yield takeLeading(Action.GetContactPersonPictureRequest, getContactPersonPicture);
}

export function* postContactPersonPicture(action: ReturnType<typeof profileSettingsActions.postContactPersonPictureRequest>) {
  try {
    const contactPersonDetails = yield* select((state: RootState) => state.generalReducer.applicationReducer.contactPersonDetails);

    const formData = new FormData();
    formData.append("file", action.payload.file);

    if (!baseUrl) throw Error("Invalid base url");
    const url = `${ContactPersonPictureService.postApiV1ContactPersonPictures.toString().replace(/[\s\S]+url: ?['"](.*?)['"][\s\S]+/, "$1")}?contactPersonId=${action.payload.contactPersonId}`;
    const result = yield* call(overlayedFetch, baseUrl, url, {
      method: "POST",
      body: formData,
      headers: {
        "Content-Disposition": "form-data;",
        ...getManualFetchHeaders()
      }
    });
    if (!result.ok) {
      const error: unknown = yield call([result, result.json]); // Array to pass result context so result.json is callable
      throw error;
    } else {
      yield put(profileSettingsActions.setIsProfilePictureDialogOpen(false));

      const base64File: string = yield fileToBase64(action.payload.file);

      // Updated cache when file is changed
      const cacheKey = CACHE_KEYS.CONTACT_PERSON_PICTURE(action.payload.contactPersonId, Shared_PictureResolutions.Large);
      yield* call(() => saveCache(cacheKey, base64File));

      if (contactPersonDetails.loading === "has-loaded") yield put(applicationActions.getContactPersonDetailsSuccess({ ...contactPersonDetails.data, profilePicture: base64File }));
    }
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchPostContactPersonPicture() {
  yield takeLeading(Action.postContactPersonPictureRequst, postContactPersonPicture);
}

export function* deleteContactPersonPicture(action: ReturnType<typeof profileSettingsActions.deleteContactPersonPictureRequest>) {
  try {
    const contactPersonDetails = yield* select((state: RootState) => state.generalReducer.applicationReducer.contactPersonDetails);

    yield* call(ContactPersonPictureService.deleteApiV1ContactPersonPictures, action.payload.contactPersonId);

    // Delte cache when file is removed
    const cacheKey = CACHE_KEYS.CONTACT_PERSON_PICTURE(action.payload.contactPersonId, Shared_PictureResolutions.Large);
    yield* call(() => deleteCache(cacheKey));

    if (contactPersonDetails.loading === "has-loaded") yield put(applicationActions.getContactPersonDetailsSuccess({ ...contactPersonDetails.data, profilePicture: "" }));
  } catch (error) {
    yield put(errorActions.addError(error));
  }
}

export function* watchdeleteContactPersonPicture() {
  yield takeLeading(Action.deleteContactPersonPictureRequest, deleteContactPersonPicture);
}

export function* profileSettingsSaga() {
  yield all([fork(watchPostContactPersonPicture), fork(watchdeleteContactPersonPicture), fork(watchgetContactPersonPicture)]);
}
